import { useSnackbar } from "notistack";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import {
  cancelSubscription,
  exportBillingUsage,
  generateCompanyInvoices,
  getCompanySubscription,
  getBillingPlanList,
  getBillingTransactions,
  getCustomerCheckout,
  getCustomerPortal,
  getLagoPlan,
  getPaymentIntent,
  getPaymentMethods,
  getPublicPlans,
  payInvoice,
  updatePaymentMethod,
} from "../api-services/billing";

export const useBillingPlanList = () => {
  return useQuery("billingPlanList", getBillingPlanList, { enabled: true });
};

export const useCompanySubscription = (companyId) => {
  return useQuery(
    ["companySubscription", companyId],
    () => getCompanySubscription(companyId),
    {
      enabled: !!companyId,
      retry: 1,
    }
  );
};

export const useCompanySubscriptions = (companyIds = []) => {
  const results = useQueries(
    companyIds.map((companyId) => ({
      queryKey: ["companySubscription", companyId],
      queryFn: () => getCompanySubscription(companyId),
      enabled: !!companyId && companyIds.length > 0,
      retry: 1,
    }))
  );

  // Transform results into a map for easier lookup
  const subscriptionsMap = {};
  results.forEach((result, index) => {
    const companyId = companyIds[index];
    if (companyId) {
      subscriptionsMap[companyId] = {
        data: result.data,
        isLoading: result.isLoading,
        error: result.error,
        isError: result.isError,
      };
    }
  });

  return {
    subscriptions: subscriptionsMap,
    isLoading: results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
  };
};

export const useLagoPortal = () => {
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(getCustomerPortal, {
    onSuccess: (resp) => {
      window.open(resp.customer.portal_url, "_blank", "noopener,noreferrer");
    },
    onError: () => {
      enqueueSnackbar("Error getting customer portal", {
        variant: "error",
      });
    },
  });
};

export const useLagoCheckout = () => {
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(getCustomerCheckout, {
    onSuccess: () => {
      // window.open(resp.customer.checkout_url, "_blank", "noopener,noreferrer");
    },
    onError: () => {
      enqueueSnackbar("Error getting checkout portal", {
        variant: "error",
      });
    },
  });
};

export const usePaymentIntent = (email) => {
  return useQuery(["paymentIntent", email], () => getPaymentIntent(email), {
    enabled: !!email,
    retry: 1,
    staleTime: 1000 * 60 * 10,
  });
};

export const useUpdatePaymentMethod = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(updatePaymentMethod, {
    onSuccess: () => {
      queryClient.invalidateQueries("paymentMethods");
      enqueueSnackbar("Payment method updated successfully", {
        variant: "success",
      });
    },
    onError: () => {
      enqueueSnackbar("Error updating payment method", {
        variant: "error",
      });
    },
  });
};

export const useExportBillingUsage = () => {
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(() => exportBillingUsage(), {
    onSuccess: (response) => {
      /**
       * * Create blob from CSV response and trigger download
       */
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      /**
       * * Extract filename from Content-Disposition header or use default
       */
      const filename = `billing-export-${
        new Date().toISOString().split("T")[0]
      }.csv`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () => {
      enqueueSnackbar("Error exporting billing usage", {
        variant: "error",
      });
    },
  });
};

export const usePaymentMethods = () => {
  return useQuery("paymentMethods", getPaymentMethods, {
    enabled: true,
    onError: (error) => {
      console.error("Payment methods API error:", error);
    },
  });
};

export const useBillingTransactions = () => {
  return useQuery("billingTransactions", getBillingTransactions, {
    enabled: true,
  });
};

export const useCurrentCompanyInvoice = (companyId) => {
  return useQuery(
    ["currentCompanyInvoice", companyId],
    () => generateCompanyInvoices(companyId),
    {
      enabled: !!companyId,
      retry: 1,
    }
  );
};

export const usePayInvoice = () => {
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(payInvoice, {
    onSuccess: (response) => {
      if (response.success && response.payment_url) {
        window.open(response.payment_url, "_blank", "noopener,noreferrer");
        enqueueSnackbar(response.message || "Payment URL opened in new tab", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(response.message || "Failed to generate payment URL", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      console.error("Payment URL generation error:", error);
      const errorMessage =
        error?.response?.data?.message || "Error generating payment URL";
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    },
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(cancelSubscription, {
    onSuccess: () => {
      queryClient.invalidateQueries("billingPlanList");
      queryClient.invalidateQueries("companySubscription");
      enqueueSnackbar("Subscription cancelled successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Error cancelling subscription";
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    },
  });
};

export const usePublicPlans = () => {
  return useQuery("publicPlans", getPublicPlans, {
    enabled: true,
  });
};

export const useLagoPlan = (planCode, enabled = true) => {
  return useQuery(["lagoPlan", planCode], () => getLagoPlan(planCode), {
    enabled: enabled && !!planCode,
    retry: 2,
    onError: (error) => {
      console.error(`Lago plan API error for ${planCode}:`, error);
    },
  });
};
